import Phaser from "phaser";
import { Preferences } from "@capacitor/preferences";
import type { BgmKey, SfxKey } from "@phaser/core/audio/audioCatalog";

const STORAGE_KEY_MUTED = "monspot-audio-muted";
const STORAGE_KEY_BGM_VOLUME = "monspot-bgm-volume";
const STORAGE_KEY_SFX_VOLUME = "monspot-sfx-volume";

interface AudioState {
  bgmVolume: number;
  sfxVolume: number;
  muted: boolean;
}

/**
 * 전역 오디오 매니저 (싱글톤)
 * - 어디서든 import 해서 사용 가능
 * - 먼저 아무 씬에서나 AudioManager.init(scene) 한 번만 호출해두면 됨
 */
export class AudioManager {
  private static sound?: Phaser.Sound.BaseSoundManager;
  private static bgm?: Phaser.Sound.BaseSound;
  private static state: AudioState = {
    bgmVolume: 0.8,
    sfxVolume: 1.0,
    muted: false,
  };
  private static appPaused = false;

  static pauseForBackground() {
    this.appPaused = true;
    this.sound?.pauseAll();
    if (this.bgm && this.bgm.isPlaying) this.bgm.pause();
  }

  static resumeFromBackground() {
    this.appPaused = false;
    if (this.state.muted) return; // 음소거면 복구하지 않음

    // 전체 재개(지원되면)
    this.sound?.resumeAll?.();

    // bgm은 보강
    if (this.bgm && this.bgm.isPaused) this.bgm.resume();
  }

  /**
   * 아무 씬에서나 한 번만 호출해 두면,
   * 이후부터는 전역으로 AudioManager를 사용할 수 있음.
   */
  static async init(scene: Phaser.Scene) {
    if (this.sound) return;
    this.sound = scene.sound;

    try {
      const muted = await Preferences.get({ key: STORAGE_KEY_MUTED });
      const bgmVol = await Preferences.get({ key: STORAGE_KEY_BGM_VOLUME });
      const sfxVol = await Preferences.get({ key: STORAGE_KEY_SFX_VOLUME });

      if (muted.value !== null) this.state.muted = muted.value === "true";
      if (bgmVol.value !== null) this.state.bgmVolume = Number(bgmVol.value);
      if (sfxVol.value !== null) this.state.sfxVolume = Number(sfxVol.value);
    } catch {}

    if (this.sound) {
      this.sound.mute = this.state.muted;
      this.sound.volume = 1;
    }
  }

  // -----------------------
  // BGM 제어
  // -----------------------

  static playBgm(key: BgmKey, config?: Phaser.Types.Sound.SoundConfig) {
    if (!this.sound) return;

    // 이미 같은 BGM이 재생 중이면 패스
    if (this.bgm && this.bgm.key === key) {
      if (!this.bgm.isPlaying && !this.state.muted) {
        this.bgm.play();
      }
      return;
    }

    // 기존 BGM 정리
    if (this.bgm) {
      this.bgm.stop();
      this.bgm.destroy();
      this.bgm = undefined;
    }

    this.bgm = this.sound.add(key, {
      loop: true,
      volume: this.state.bgmVolume,
      ...config,
    });

    if (!this.state.muted && !this.appPaused) {
      this.bgm.play();
    }
  }

  static stopBgm() {
    if (this.bgm) {
      this.bgm.stop();
      this.bgm.destroy();
      this.bgm = undefined;
    }
  }

  // -----------------------
  // SFX 제어
  // -----------------------

  static playSfx(key: SfxKey, config?: Phaser.Types.Sound.SoundConfig) {
    if (!this.sound || this.state.muted) return;

    this.sound.play(key, {
      volume: this.state.sfxVolume,
      ...config,
    });
  }

  static async setSfxVolume(volume: number) {
    this.state.sfxVolume = Phaser.Math.Clamp(volume, 0, 1);

    await Preferences.set({
      key: STORAGE_KEY_SFX_VOLUME,
      value: String(this.state.sfxVolume),
    });
  }

  // -----------------------
  // 음소거 / 상태
  // -----------------------

  static async setMuted(muted: boolean) {
    this.state.muted = muted;

    if (this.sound) {
      this.sound.mute = muted;
    }

    await Preferences.set({
      key: STORAGE_KEY_MUTED,
      value: String(muted),
    });

    if (this.bgm) {
      if (muted && this.bgm.isPlaying) {
        this.bgm.pause();
      } else if (!muted && this.bgm.isPaused && !this.appPaused) {
        this.bgm.resume();
      }
    }
  }

  static async toggleMute(): Promise<boolean> {
    await this.setMuted(!this.state.muted);
    return this.state.muted;
  }

  static isMuted(): boolean {
    return this.state.muted;
  }

  static getVolumes() {
    return {
      bgm: this.state.bgmVolume,
      sfx: this.state.sfxVolume,
    };
  }
}
